type Maybe<T> = T | null | undefined;

type JsonLdNode = Record<string, unknown> & {
  '@type': string;
};

type JsonLdGraph = {
  '@context': 'https://schema.org';
  '@graph': JsonLdNode[];
};

type StructuredDataRecord = Record<string, unknown>;
type TeamMemberLike = {
  firstname?: string | null;
  lastname?: string | null;
  name?: string | null;
  role?: string | null;
  eMail?: string | null;
  phone?: string | null;
};

type ContentModulesInput = unknown[] | null | undefined;

function toArray<T>(value: Maybe<T | T[]>): T[] {
  if (Array.isArray(value)) {
    return value.filter(Boolean) as T[];
  }

  return value ? [value] : [];
}

function compact<T extends Record<string, unknown>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce<Partial<T>>((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key as keyof T] = value as T[keyof T];
    }

    return acc;
  }, {});
}

function createGraph(nodes: JsonLdNode[]): JsonLdGraph | null {
  if (!nodes.length) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@graph': nodes
  };
}

function toImageUrl(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const image = value as Record<string, unknown>;
  return typeof image.url === 'string' ? image.url : undefined;
}

function hasUsableKeys(node: Maybe<Record<string, unknown>>): node is Record<string, unknown> {
  if (!node || typeof node !== 'object') {
    return false;
  }

  const keys = Object.keys(node);
  return keys.some((key) => key !== '@type');
}

function getTeamModulePeople(contentModules: ContentModulesInput) {
  const members: TeamMemberLike[] = [];

  toArray(contentModules).forEach((module) => {
    if (!module || typeof module !== 'object') {
      return;
    }

    const typedModule = module as Record<string, unknown>;
    if (typedModule.__typename !== 'ContentTeamRecord') {
      return;
    }

    const featuredTeamMembers = typedModule.featuredTeamMembers as
      | TeamMemberLike[]
      | TeamMemberLike
      | null
      | undefined;

    members.push(...toArray(featuredTeamMembers));
  });

  return members;
}

function buildFallbackName(member: TeamMemberLike) {
  return [member.firstname, member.lastname].filter(Boolean).join(' ');
}

function buildPeopleNodes(contentModules: ContentModulesInput): JsonLdNode[] {
  const members = getTeamModulePeople(contentModules);
  const uniquePeople = new Map<string, TeamMemberLike>();

  members.forEach((member) => {
    const fallbackName = buildFallbackName(member);
    const name = member.name ?? fallbackName;
    const key = member.eMail ?? name;

    if (!key) {
      return;
    }

    if (!uniquePeople.has(key)) {
      uniquePeople.set(key, member);
    }
  });

  return Array.from(uniquePeople.values())
    .map((member) => {
      const fallbackName = buildFallbackName(member);

      return compact({
        '@type': 'Person',
        name: member.name ?? (fallbackName || undefined),
        jobTitle: member.role,
        email: member.eMail,
        telephone: member.phone
      }) as JsonLdNode;
    })
    .filter((personNode) => hasUsableKeys(personNode));
}

export { buildPeopleNodes as buildPeopleJsonLdNodesFromContentModules };

export function buildServiceJsonLdNode(
  service: { title?: string | null } | null | undefined,
  url: string
): JsonLdNode | null {
  const node = compact({
    '@type': 'Service',
    name: service?.title,
    url
  }) as JsonLdNode;

  return hasUsableKeys(node) ? node : null;
}

export function buildPageStructuredDataJsonLd(
  options: {
    contentModules?: unknown[] | null;
    service?: { title?: string | null } | null;
    serviceUrl?: string | null;
  } = {}
): JsonLdGraph | null {
  const graph: JsonLdNode[] = [];

  graph.push(...buildPeopleNodes(options.contentModules));

  if (options.serviceUrl) {
    const serviceNode = buildServiceJsonLdNode(options.service, options.serviceUrl);
    if (serviceNode) {
      graph.push(serviceNode);
    }
  }

  return createGraph(graph);
}

function buildOrganizationNode(input: Maybe<StructuredDataRecord>): JsonLdNode | null {
  if (!input) {
    return null;
  }

  const parentOrganization = buildOrganizationNode(
    (input.parentOrganization as Maybe<StructuredDataRecord>) ?? null
  );

  const node = compact({
    '@type': 'Organization',
    '@id': input.id,
    name: input.name,
    legalName: input.legalName,
    description: input.description,
    url: input.url,
    email: input.email,
    telephone: input.telephone,
    sameAs: input.sameAs,
    logo: toImageUrl(input.logo),
    image: toImageUrl(input.image),
    parentOrganization: parentOrganization ?? undefined
  });

  return hasUsableKeys(node) ? (node as JsonLdNode) : null;
}

function buildPostalAddressNode(input: Maybe<StructuredDataRecord>): JsonLdNode | null {
  if (!input) {
    return null;
  }

  const node = compact({
    '@type': 'PostalAddress',
    streetAddress: input.streetAddress,
    addressLocality: input.addressLocality,
    postalCode: input.postalCode,
    addressRegion: input.addressRegion,
    addressCountry: input.addressCountry
  });

  return hasUsableKeys(node) ? (node as JsonLdNode) : null;
}

function buildLocalBusinessNode(input: Maybe<StructuredDataRecord>): JsonLdNode | null {
  if (!input) {
    return null;
  }

  const address = buildPostalAddressNode((input.address as Maybe<StructuredDataRecord>) ?? null);
  const department = toArray(input.department as Maybe<StructuredDataRecord | StructuredDataRecord[]>)
    .map((item) => buildLocalBusinessNode(item))
    .filter(Boolean) as JsonLdNode[];

  const node = compact({
    '@type': 'LocalBusiness',
    '@id': input.id,
    name: input.name,
    description: input.description,
    url: input.url,
    email: input.email,
    telephone: input.telephone,
    sameAs: input.sameAs,
    image: toImageUrl(input.image),
    priceRange: input.priceRange,
    openingHours: input.openingHours,
    currenciesAccepted: input.currenciesAccepted,
    address: address ?? undefined,
    department: department.length > 0 ? department : undefined
  });

  return hasUsableKeys(node) ? (node as JsonLdNode) : null;
}

function buildServiceNode(input: Maybe<StructuredDataRecord>): JsonLdNode | null {
  if (!input) {
    return null;
  }

  const node = compact({
    '@type': 'Service',
    '@id': input.id,
    name: input.name,
    description: input.description,
    serviceType: input.serviceType,
    areaServed: input.areaServed,
    availableChannel: input.availableChannel,
    termsOfService: input.termsOfService,
    providerName: input.providerName,
    url: input.url
  });

  return hasUsableKeys(node) ? (node as JsonLdNode) : null;
}

function buildPersonNode(input: Maybe<StructuredDataRecord>): JsonLdNode | null {
  if (!input) {
    return null;
  }

  const node = compact({
    '@type': 'Person',
    '@id': input.id,
    name: input.name,
    givenName: input.givenName,
    familyName: input.familyName,
    jobTitle: input.role,
    email: input.eMail,
    telephone: input.phone,
    url: input.url,
    sameAs: input.sameAs,
    image: toImageUrl(input.image)
  });

  return hasUsableKeys(node) ? (node as JsonLdNode) : null;
}

export function buildStructuredDataJsonLd(structuredDataModel: Maybe<StructuredDataRecord>): JsonLdGraph | null {
  if (!structuredDataModel) {
    return null;
  }

  const graph: JsonLdNode[] = [];

  const organizations = toArray(
    structuredDataModel.organization as Maybe<StructuredDataRecord | StructuredDataRecord[]>
  )
    .map((item) => buildOrganizationNode(item))
    .filter(Boolean) as JsonLdNode[];

  const localBusinesses = toArray(
    structuredDataModel.localBusiness as Maybe<StructuredDataRecord | StructuredDataRecord[]>
  )
    .map((item) => buildLocalBusinessNode(item))
    .filter(Boolean) as JsonLdNode[];

  const services = toArray(
    structuredDataModel.services as Maybe<StructuredDataRecord | StructuredDataRecord[]>
  )
    .map((item) => buildServiceNode(item))
    .filter(Boolean) as JsonLdNode[];

  const members = toArray(
    structuredDataModel.teamMembers as Maybe<StructuredDataRecord | StructuredDataRecord[]>
  )
    .map((item) => buildPersonNode(item))
    .filter(Boolean) as JsonLdNode[];

  graph.push(...organizations, ...localBusinesses, ...services, ...members);

  return createGraph(graph);
}
